import type { MeasurementData } from '../../store/reducers/workspace.reducer';
import type { RootState } from '../../store/store';
import type { LampSimilarityDetail, Version } from '../../types/project';
import { isFullyCatalogueAssigned } from '../lampMarketMatch';
import { FRONTEND_MEASUREMENT_CALCULATION_FLAG } from './types';

export type MarketMatchPreviewMode = 'ml' | 'frontend';

/** Whether the workspace should use frontend-computed measurement results by default. */
export const shouldUseFrontendMeasurementSource = (
  userFeatureFlags: readonly string[],
  version: Version | null | undefined,
): boolean => userFeatureFlags.includes(FRONTEND_MEASUREMENT_CALCULATION_FLAG) || isFullyCatalogueAssigned(version);

/** How a market-match candidate preview should be rendered, if it can be applied at all. */
export const getMarketMatchPreviewMode = (
  userFeatureFlags: readonly string[],
  detail: LampSimilarityDetail | undefined,
  version: Version | null | undefined,
): MarketMatchPreviewMode | null => {
  if (detail?.measurementData?.length) return 'ml';
  if (shouldUseFrontendMeasurementSource(userFeatureFlags, version)) return 'frontend';
  return null;
};

export const canApplyMarketMatchCandidate = (
  userFeatureFlags: readonly string[],
  detail: LampSimilarityDetail | undefined,
  version: Version | null | undefined,
): boolean => getMarketMatchPreviewMode(userFeatureFlags, detail, version) !== null;

/** Measurement data shown in the workspace (respects source + market-match preview). */
export const selectWorkspaceMeasurementData = (state: RootState): MeasurementData[] | null => {
  const { workspace } = state;
  const activeVersion = workspace.project?.versions?.find((v) => v.id === workspace.project?.activeVersionId);
  const applied = workspace.appliedLampSimilaritySelection;

  if (applied) {
    if (applied.previewMode === 'frontend') {
      return workspace.frontendMeasurementData ?? null;
    }
    return activeVersion?.measurementData ?? null;
  }
  if (workspace.measurementSource === 'frontend') {
    return workspace.frontendMeasurementData ?? null;
  }
  return activeVersion?.measurementData ?? null;
};
